// ============================================================================
// Overall Control — SLA settings tab (admins only).
//
// Every number the SLA engine uses, editable here, with the user's sheet as the defaults. Saving
// stores a new version from today, with who changed what (shown under "Change history"). The
// backend owns the list of settings, so a new setting appears here without a frontend change.
// ============================================================================
import { Alert, Button, Input, InputNumber, message, Select, Skeleton, Switch, Table, Tag, TimePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { getSlaSettings, saveSlaSettings } from "../../api/orders";
import { SlaDayClock, SlaField, SlaSettings, SlaValue } from "../../types/sla";
import { Section } from "../real/common";
import styles from "../../index.module.scss";

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const fmtValue = (v: SlaValue | null | undefined, field?: SlaField, users?: Map<string, string>): string => {
  if (v == null) return "–";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "none";
  if (typeof v === "object") return `Day +${(v as SlaDayClock).day}, ${(v as SlaDayClock).time}`;
  if (field?.type === "minutes") return `${v} min`;
  if (field?.type === "days") return `${v} day${v === 1 ? "" : "s"}`;
  if (field?.type === "user") return users?.get(String(v)) ?? String(v);
  return String(v);
};

const SlaInput: React.FC<{
  field: SlaField;
  value: SlaValue;
  disabled: boolean;
  options: SlaSettings["options"];
  onChange: (v: SlaValue) => void;
}> = ({ field, value, disabled, options, onChange }) => {
  switch (field.type) {
    case "minutes":
    case "days":
      return (
        <InputNumber
          size="small"
          min={0}
          max={field.type === "minutes" ? 10080 : 30}
          precision={0}
          value={value as number}
          disabled={disabled}
          addonAfter={field.type === "minutes" ? "min" : "days"}
          onChange={(v) => onChange(v ?? 0)}
          style={{ width: 150 }}
        />
      );
    case "clock":
      return (
        <TimePicker
          size="small"
          format="HH:mm"
          minuteStep={5}
          allowClear={false}
          disabled={disabled}
          value={dayjs(String(value), "HH:mm")}
          onChange={(t) => t && onChange(t.format("HH:mm"))}
        />
      );
    case "day_clock": {
      const dc = value as SlaDayClock;
      return (
        <span className={styles.slaDayClock}>
          <InputNumber
            size="small"
            min={0}
            max={7}
            precision={0}
            addonBefore="Day +"
            value={dc.day}
            disabled={disabled}
            onChange={(d) => onChange({ ...dc, day: d ?? 0 })}
            style={{ width: 110 }}
          />
          <TimePicker
            size="small"
            format="HH:mm"
            minuteStep={5}
            allowClear={false}
            disabled={disabled}
            value={dayjs(dc.time, "HH:mm")}
            onChange={(t) => t && onChange({ ...dc, time: t.format("HH:mm") })}
          />
        </span>
      );
    }
    case "bool":
      return <Switch size="small" checked={value as boolean} disabled={disabled} onChange={onChange} />;
    case "dates":
      return (
        <Select
          size="small"
          mode="tags"
          placeholder="YYYY-MM-DD, Enter to add"
          value={value as string[]}
          disabled={disabled}
          onChange={(v) => onChange((v as string[]).filter((d) => dayjs(d, "YYYY-MM-DD", true).isValid()))}
          style={{ minWidth: 260 }}
        />
      );
    case "teams":
      return (
        <Select size="small" mode="multiple" options={options.teams} value={value as string[]} disabled={disabled} onChange={onChange} style={{ minWidth: 260 }} />
      );
    case "user":
      return (
        <Select size="small" showSearch optionFilterProp="label" options={options.users} value={value as string} disabled={disabled} onChange={onChange} style={{ minWidth: 300 }} />
      );
    default:
      return <span>{fmtValue(value)}</span>;
  }
};

// `initial`: the payload the page already fetched to decide whether to show this tab (saves a second GET).
const SlaSettingsTab: React.FC<{ initial?: SlaSettings | null }> = ({ initial }) => {
  const [data, setData] = useState<SlaSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, SlaValue>>({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const load = (fresh?: SlaSettings) => {
    const apply = (d: SlaSettings) => {
      setData(d);
      setDraft({});
      setNote("");
      setFieldErrors({});
    };
    if (fresh) return apply(fresh);
    getSlaSettings()
      .then(apply)
      .catch((e) => setError(e?.response?.data?.detail ?? e?.message ?? "Could not load the SLA settings"));
  };
  useEffect(() => load(initial ?? undefined), []); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = useMemo(() => (data ? data.stages.flatMap((s) => s.sections.flatMap((x) => x.fields)) : []), [data]);
  const users = useMemo(() => new Map((data?.options.users ?? []).map((u) => [u.value, u.label])), [data]);
  const changed = fields.filter((f) => f.key in draft && !same(draft[f.key], f.value));

  if (error) return <Alert type="error" showIcon message={error} className={styles.inlineAlert} />;
  if (!data) return <Skeleton active paragraph={{ rows: 10 }} />;
  const editable = data.can_edit && data.storage_ready;

  const save = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const res = await saveSlaSettings(Object.fromEntries(changed.map((f) => [f.key, draft[f.key]])), note);
      message.success(res.saved ? "SLA settings saved. They apply from today." : "Nothing changed.");
      load(res);
    } catch (e: any) {
      setFieldErrors(e?.response?.data?.errors ?? {});
      message.error(e?.response?.data?.detail ?? "Could not save");
    } finally {
      setSaving(false);
    }
  };
  const resetToDefaults = () =>
    setDraft(Object.fromEntries(fields.filter((f) => !same(f.default, f.value)).map((f) => [f.key, f.default])));

  return (
    <div>
      {!data.storage_ready ? (
        <Alert
          type="info"
          showIcon
          className={styles.inlineAlert}
          message="Showing the sheet defaults. Saving becomes available once the SLA table is created on deploy (its migration)."
        />
      ) : null}
      <div className={styles.panel}>
        <div className={styles.slaHeader}>
          <div>
            <div className={styles.strong}>
              {data.current
                ? `Rules in force since ${dayjs(data.current.effective_from).format("D MMM YYYY")}`
                : "Sheet defaults (not changed yet)"}
            </div>
            <div className={styles.mutedSmall}>
              {data.current
                ? `Saved by ${data.current.created_by} on ${dayjs(data.current.created_at).format("D MMM, HH:mm")}${data.current.note ? ` · “${data.current.note}”` : ""}`
                : "A change applies from the day it is saved; earlier days keep the rules they had. Every save is emailed to the dashboard owner."}
            </div>
          </div>
          <Button size="small" disabled={!editable} onClick={resetToDefaults}>
            Reset all to sheet defaults
          </Button>
        </div>
      </div>

      {data.stages.map((stage) => (
        <Section key={stage.key} title={stage.label}>
          {stage.notes.length ? (
            <ul className={styles.slaNotes}>
              {stage.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          ) : null}
          {stage.sections.map((section) => (
            <div key={section.key} className={styles.slaSection}>
              <div className={styles.slaSectionTitle}>{section.label}</div>
              <Table<SlaField>
                size="small"
                rowKey="key"
                pagination={false}
                showHeader={false}
                className={styles.denseTable}
                dataSource={section.fields}
                rowClassName={(f) => (f.key in draft && !same(draft[f.key], f.value) ? styles.slaChangedRow : "")}
                columns={[
                  {
                    key: "label",
                    width: "40%",
                    render: (_, f) =>
                      f.help ? (
                        <Tooltip title={f.help}>
                          <span className={styles.helpHead}>{f.label}</span>
                        </Tooltip>
                      ) : (
                        f.label
                      ),
                  },
                  {
                    key: "value",
                    render: (_, f) => (
                      <div>
                        <SlaInput
                          field={f}
                          value={f.key in draft ? draft[f.key] : f.value}
                          disabled={!editable}
                          options={data.options}
                          onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                        />
                        {fieldErrors[f.key] ? <div className={styles.slaError}>{fieldErrors[f.key]}</div> : null}
                      </div>
                    ),
                  },
                  {
                    key: "default",
                    width: 220,
                    render: (_, f) =>
                      same(f.value, f.default) && !(f.key in draft && !same(draft[f.key], f.default)) ? (
                        <span className={styles.mutedSmall}>sheet default</span>
                      ) : (
                        <span className={styles.mutedSmall}>sheet default: {fmtValue(f.default, f, users)}</span>
                      ),
                  },
                ]}
              />
            </div>
          ))}
        </Section>
      ))}

      <Section title="Change history">
        {data.history.length ? (
          <Table
            size="small"
            rowKey="id"
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            className={styles.denseTable}
            dataSource={data.history}
            columns={[
              { title: "Saved", dataIndex: "created_at", width: 150, render: (v: string) => dayjs(v).format("D MMM YYYY, HH:mm") },
              { title: "By", dataIndex: "created_by", width: 160 },
              { title: "Applies from", dataIndex: "effective_from", width: 120, render: (v: string) => dayjs(v).format("D MMM YYYY") },
              {
                title: "Changes",
                dataIndex: "changes",
                render: (cs: SlaSettings["history"][number]["changes"]) =>
                  cs.map((c) => {
                    const f = fields.find((x) => x.key === c.key);
                    return (
                      <div key={c.key}>
                        {c.label}: <span className={styles.muted}>{fmtValue(c.before, f, users)}</span> → <strong>{fmtValue(c.after, f, users)}</strong>
                      </div>
                    );
                  }),
              },
              { title: "Note", dataIndex: "note", width: 220 },
            ]}
          />
        ) : (
          <div className={styles.empty}>No changes yet: the sheet defaults are in force.</div>
        )}
      </Section>

      {editable && changed.length ? (
        <div className={styles.slaSaveBar}>
          <Tag color="blue">{changed.length} change{changed.length === 1 ? "" : "s"}</Tag>
          <Input size="small" placeholder="Why (shown in the history)" value={note} onChange={(e) => setNote(e.target.value)} style={{ maxWidth: 360 }} />
          <Button size="small" onClick={() => setDraft({})}>
            Discard
          </Button>
          <Button size="small" type="primary" loading={saving} disabled={!note.trim()} onClick={save}>
            Save · applies from today
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default SlaSettingsTab;
